import React from "react";
import "./DesignerRenderer.css";

import useDragMove from "./hooks/useDragMove";
import useResizeMove from "./hooks/useResizeMove";
import ResizeHandles from "./shared/ResizeHandles";
import ObjectQuickMenu from "./shared/ObjectQuickMenu";

import TextRenderer from "./object-renderers/TextRenderer";
import ImageRenderer from "./object-renderers/ImageRenderer";
import ShapeRenderer from "./object-renderers/ShapeRenderer";
import ButtonRenderer from "./object-renderers/ButtonRenderer";
import TagRenderer from "./object-renderers/TagRenderer";

const DesignerRenderer = ({
  object,
  latestTags,
  selected,
  selectedGroupId = null,
  objects = [],
  onSelect,
  updateObjectPosition,
  updateObjectRect,
  updateObjectsBulk,
  updateObjectDataField,
  onButtonAction,
  onDuplicateObject,
  onDeleteObject,
  onBringForwardObject,
  onSendBackwardObject,
  onBringToFrontObject,
  onSendToBackObject,
  canvasRef,
  readOnly = false,
}) => {
  const styleJson = object.style_json || {};
  const dataJson = object.data_json || {};

  const dragDisabled =
    readOnly ||
    object.locked ||
    (object.type === "shape" && dataJson.shape_type === "custom" && selected);

  const resizeDisabled = readOnly || object.locked;

  const { isDragging, handleMouseDown } = useDragMove({
    object,
    onSelect,
    updateObjectPosition,
    objects,
    selectedGroupId,
    disabled: dragDisabled,
  });

  const { handleResizeStart } = useResizeMove({
    object,
    objects,
    selectedGroupId,
    onSelect,
    updateObjectRect,
    updateObjectsBulk,
    disabled: resizeDisabled,
  });

  const commonStyle = {
    left: `${object.x}px`,
    top: `${object.y}px`,
    width: `${object.width}px`,
    height: `${object.height}px`,
    opacity: object.opacity ?? 1,
    transform: `rotate(${object.rotation || 0}deg)`,
    zIndex: object.z_index ?? 0,
    display: object.visible === false ? "none" : "block",
    color: styleJson.color || "#111827",
    cursor: readOnly ? "default" : isDragging ? "grabbing" : "grab",
  };

  const renderSelectionHandles = () => {
    if (!selected || object.locked || readOnly) return null;
    return <ResizeHandles onResizeStart={handleResizeStart} />;
  };

  const renderQuickMenu = () => {
    if (!selected || readOnly) return null;

    return (
      <ObjectQuickMenu
        object={object}
        canvasRef={canvasRef}
        onDuplicate={onDuplicateObject}
        onDelete={onDeleteObject}
        onBringForward={onBringForwardObject}
        onSendBackward={onSendBackwardObject}
        onBringToFront={onBringToFrontObject}
        onSendToBack={onSendToBackObject}
      />
    );
  };

  const sharedProps = {
    object,
    latestTags,
    styleJson,
    dataJson,
    commonStyle,
    handleMouseDown,
    renderSelectionHandles,
  };

  if (object.type === "text") {
    return (
      <>
        <TextRenderer {...sharedProps} />
        {renderQuickMenu()}
      </>
    );
  }

  if (object.type === "image") {
    return (
      <>
        <ImageRenderer {...sharedProps} />
        {renderQuickMenu()}
      </>
    );
  }

  if (object.type === "tag") {
    return (
      <>
        <TagRenderer {...sharedProps} onButtonAction={onButtonAction} />
        {renderQuickMenu()}
      </>
    );
  }

  if (object.type === "shape") {
    return (
      <>
        <ShapeRenderer
          {...sharedProps}
          selected={selected}
          readOnly={readOnly}
          updateObjectDataField={updateObjectDataField}
          updateObjectPosition={updateObjectPosition}
          onSelect={onSelect}
        />
        {renderQuickMenu()}
      </>
    );
  }

  if (object.type === "button") {
    return (
      <>
        <ButtonRenderer
          {...sharedProps}
          isDragging={isDragging}
          onButtonAction={onButtonAction}
        />
        {renderQuickMenu()}
      </>
    );
  }

  return null;
};

export default DesignerRenderer;